import React from 'react';

type CenteredLoadingSpinnerProps = {
  isDarkMode: boolean;
  /**
   * inline: centers within normal flow
   * overlay: covers the nearest positioned parent (position: relative)
   */
  variant?: 'inline' | 'overlay';
  minHeight?: React.CSSProperties['minHeight'];
  /** For overlay: solid page bg vs translucent dim */
  overlayBackground?: 'solid' | 'dim';
  sizePx?: number;
  wrapperDisplay?: React.CSSProperties['display'];
};

const CenteredLoadingSpinner: React.FC<CenteredLoadingSpinnerProps> = ({
  isDarkMode,
  variant = 'inline',
  minHeight,
  overlayBackground = 'solid',
  sizePx = 44,
  wrapperDisplay,
}) => {
  const wrapperStyle: React.CSSProperties = {
    display: wrapperDisplay ?? 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(typeof minHeight !== 'undefined' ? { minHeight } : {}),
    ...(variant === 'overlay'
      ? {
          position: 'absolute',
          inset: 0,
          zIndex: 20,
          backgroundColor:
            overlayBackground === 'dim'
              ? isDarkMode
                ? 'rgba(17, 24, 39, 0.65)'
                : 'rgba(255, 255, 255, 0.65)'
              : isDarkMode
                ? '#111827'
                : '#ffffff',
        }
      : {}),
  };

  const spinnerStyle: React.CSSProperties = {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: '9999px',
    border: `4px solid ${isDarkMode ? 'rgba(229, 231, 235, 0.25)' : 'rgba(17, 24, 39, 0.15)'}`,
    borderTopColor: isDarkMode ? '#e5e7eb' : '#0D0D0D',
    animation: 'centered-loading-spinner-spin 0.9s linear infinite',
  };

  return (
    <>
      <div style={wrapperStyle} aria-label="Loading" role="status">
        <div style={spinnerStyle} />
      </div>
      <style>{`
        @keyframes centered-loading-spinner-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CenteredLoadingSpinner;
